import { ApiNonAttendeeRegistrationParticipant } from "shared/components/searchNonAttendeeRegistrationForm/types";
import { useGetRegFieldSchemaByViewType } from "shared/dataloader";
import React from "react";
import FieldViewType from "shared/enums/FieldViewType";
import FieldAlias from "shared/form/renderer/enums/FieldAlias";
import { useReducerWrapper } from "shared/hooks/useReducerWrapper";
import { useAssignTicket, useAssignTicketToNonAttendee } from "./dataloader";
import Reducer from "./reducer";
import { AssignTicketProps, AssignTicketService, ReducerDispatch, State } from "./types";

export default function useAssignTicketService({
  initialState = { step: 0 },
  ...props
}: AssignTicketProps): AssignTicketService {
  const [state, dispatch] = useReducerWrapper<State, ReducerDispatch>(Reducer, initialState);

  const { data: assignFormSchema, dataUpdatedAt } = useGetRegFieldSchemaByViewType({
    isEnabled: !!state.workEmail,
    registrationId: props.registrationId,
    registrationParticipantId: state.nonAttendeeParticipant?.id,
    viewType: FieldViewType.TICKET_ASSIGNMENT,
    ticketFormId: props.ticketFormId
  });

  const { mutateAsync: assignTicket } = useAssignTicket({ successHandler: props.assignTicketSuccessHandler });
  const { mutateAsync: assignTicketToNonAttendee } = useAssignTicketToNonAttendee({
    successHandler: props.assignTicketSuccessHandler
  });

  const assignTicketSchemaList = React.useMemo(() => {
    if (assignFormSchema?.profile) {
      return assignFormSchema?.profile.map(schemaObj => {
        if (schemaObj.alias === FieldAlias.WORK_EMAIL) {
          return {
            ...schemaObj,
            value: state.workEmail,
            disabled: true
          };
        }
        return schemaObj;
      });
    } else {
      return assignFormSchema?.profile || [];
    }
  }, [assignFormSchema?.profile, state.workEmail]);

  const searchSuccessHandler = React.useCallback(
    (workEmail: string, regParticipantInfo: ApiNonAttendeeRegistrationParticipant) => {
      dispatch.saveSearchedNonAttendeeInfo(workEmail, regParticipantInfo);
    },
    [dispatch]
  );

  const submitHandler = React.useCallback(
    async values => {
      try {
        if (state.nonAttendeeParticipant) {
          await assignTicketToNonAttendee({
            ...values,
            initiatorRegistrationParticipantId: props.registrationParticipantId,
            registrationParticipantId: state.nonAttendeeParticipant.id,
            fieldViewType: assignFormSchema?.fieldViewType,
            registrationId: props.registrationId,
            bulkTicketId: props.bulkTicketId
          });
        } else {
          await assignTicket({
            ...values,
            initiatorRegistrationParticipantId: props.registrationParticipantId,
            fieldViewType: assignFormSchema?.fieldViewType,
            registrationId: props.registrationId,
            bulkTicketId: props.bulkTicketId
          });
        }
        props.handleAssignTicketSuccess?.(values[FieldAlias.WORK_EMAIL]);
      } catch (error) {}
    },
    [props, state.nonAttendeeParticipant, assignTicketToNonAttendee, assignFormSchema?.fieldViewType, assignTicket]
  );

  return {
    state,
    assignFormSchema,
    dataUpdatedAt,
    assignTicketSchemaList,
    submitHandler,
    searchSuccessHandler
  };
}
